import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Lock, ShieldCheck, KeyRound, Smartphone, Loader2, Copy, CheckCircle2 } from "lucide-react";
import SEOHead from "../components/SEOHead";

/**
 * Admin first-time setup — single-page wizard.
 *
 * URL: /admin/setup?token=<setup_token>
 *
 * Flow:
 *   1. GET  /api/auth/admin/setup/info?token=... → preview email/name (token preserved)
 *   2. GET  /api/auth/admin/setup/recovery-codes?token=... → returns QR + raw recovery codes
 *      (idempotent — safe to call multiple times until setup is finalised)
 *   3. POST /api/auth/admin/setup/finalize { setup_token, new_password, totp_otp }
 *      → activates the account, returns ok:true
 *   4. Redirect to /login for first sign-in.
 */
export default function AdminSetup() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [stage, setStage] = useState("loading"); // loading | invalid | form | done
  const [info, setInfo] = useState(null);
  const [enroll, setEnroll] = useState(null); // {qr_data_url, secret_manual, recovery_codes}
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [otp, setOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) { setStage("invalid"); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data: infoData } = await api.get(`/auth/admin/setup/info?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        setInfo(infoData);
        const { data: enrollData } = await api.get(`/auth/admin/setup/recovery-codes?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        setEnroll(enrollData);
        setStage("form");
      } catch {
        if (!cancelled) setStage("invalid");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { toast.error("Passwords do not match"); return; }
    if (!/^\d{6}$/.test(otp)) { toast.error("Enter the 6-digit code from your authenticator app"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/admin/setup/finalize", { setup_token: token, new_password: pwd, totp_otp: otp });
      setStage("done");
      toast.success("Admin account activated — please sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (stage === "loading") {
    return (
      <div className="container-x py-24 max-w-md text-center" data-testid="admin-setup-loading">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-brand-chili mb-3" />
        <p className="text-brand-muted">Loading your setup details…</p>
      </div>
    );
  }
  if (stage === "invalid") {
    return (
      <div className="container-x py-24 max-w-md text-center" data-testid="admin-setup-invalid">
        <h1 className="font-serif text-3xl text-brand-dark mb-3">Link expired</h1>
        <p className="text-brand-muted">Your admin setup link is invalid or has expired. Contact support to request a new one.</p>
      </div>
    );
  }
  if (stage === "done") {
    return (
      <div className="container-x py-24 max-w-md text-center" data-testid="admin-setup-done">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
        <h1 className="font-serif text-3xl text-brand-dark mb-2">All set!</h1>
        <p className="text-brand-muted">Redirecting to sign in…</p>
      </div>
    );
  }

  return (
    <div className="container-x py-16 lg:py-20 max-w-2xl" data-testid="admin-setup-page">
      <SEOHead title="Admin setup | Sri Durga Foods" noindex />
      <div className="text-center mb-10">
        <span className="overline mb-3 block">Admin Setup</span>
        <h1 className="font-serif text-4xl text-brand-dark">Activate your admin account</h1>
        <p className="text-sm text-brand-muted mt-3">
          for <strong className="text-brand-dark">{info?.email}</strong>
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Step 1: Set password */}
        <section className="bg-white rounded-2xl border border-brand-line p-6">
          <h2 className="font-serif text-xl text-brand-dark mb-1 flex items-center gap-2">
            <Lock className="w-5 h-5 text-brand-chili" strokeWidth={1.6} /> 1. Choose a password
          </h2>
          <p className="text-xs text-brand-muted mb-4">
            10+ chars, mixed case, a digit and a special character.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input type="password" className="input-field" autoComplete="new-password"
              placeholder="New password" value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="setup-password" />
            <input type="password" className="input-field" autoComplete="new-password"
              placeholder="Confirm password" value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              data-testid="setup-password-confirm" />
          </div>
        </section>

        {/* Step 2: Scan QR */}
        <section className="bg-white rounded-2xl border border-brand-line p-6">
          <h2 className="font-serif text-xl text-brand-dark mb-1 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-brand-chili" strokeWidth={1.6} /> 2. Set up two-factor authentication
          </h2>
          <p className="text-xs text-brand-muted mb-4">
            Open Google Authenticator (or Authy / 1Password) and scan this QR code.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-5">
            {enroll?.qr_data_url && (
              <img src={enroll.qr_data_url} alt="2FA QR code"
                className="w-48 h-48 border border-brand-line rounded-lg bg-white"
                data-testid="setup-qr" />
            )}
            <div className="flex-1 text-xs">
              <p className="text-brand-muted mb-2">Can&apos;t scan? Enter this secret manually:</p>
              <div className="font-mono text-[11px] bg-brand-cream/60 border border-brand-line rounded px-3 py-2 break-all" data-testid="setup-secret">
                {enroll?.secret_manual}
              </div>
            </div>
          </div>
        </section>

        {/* Step 3: Recovery codes */}
        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <h2 className="font-serif text-xl text-amber-900 mb-1 flex items-center gap-2">
            <KeyRound className="w-5 h-5" strokeWidth={1.6} /> 3. Save your recovery codes
          </h2>
          <p className="text-xs text-amber-800 mb-4">
            Use any of these one-time codes if you lose access to your authenticator app. <strong>They will NEVER be shown again.</strong>
          </p>
          {enroll?.recovery_codes_already_revealed ? (
            <div className="bg-white border border-amber-300 rounded p-4 text-sm text-amber-900">
              ⚠️ Recovery codes were already revealed once for this setup link. If you didn&apos;t save them,
              re-generate them from <em>Admin → Security</em> after activation.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-xs" data-testid="setup-recovery-codes">
                {(enroll?.recovery_codes || []).map((c) => (
                  <div key={c} className="bg-white border border-amber-200 rounded px-2 py-1.5 text-center">{c}</div>
                ))}
              </div>
              <button type="button"
                onClick={() => {
                  navigator.clipboard.writeText((enroll?.recovery_codes || []).join("\n"));
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-900 font-medium hover:underline"
                data-testid="setup-copy-codes">
                <Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy all codes"}
              </button>
            </>
          )}
        </section>

        {/* Step 4: Confirm OTP */}
        <section className="bg-white rounded-2xl border border-brand-line p-6">
          <h2 className="font-serif text-xl text-brand-dark mb-1 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-brand-chili" strokeWidth={1.6} /> 4. Confirm with a code
          </h2>
          <p className="text-xs text-brand-muted mb-4">
            Enter the 6-digit code from your authenticator app to finish setup.
          </p>
          <input type="text" inputMode="numeric" pattern="\d{6}" maxLength={6}
            className="input-field font-mono text-center text-2xl tracking-widest"
            placeholder="000000" value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            data-testid="setup-otp" />
        </section>

        <button type="submit" disabled={submitting}
          className="btn-primary w-full"
          data-testid="setup-submit">
          {submitting ? "Activating…" : "Activate admin account"}
        </button>
      </form>
    </div>
  );
}
